import type { Channel } from 'stream-chat';

export type ChannelMemberRow = {
  id: string;
  name: string;
  online: boolean;
};

/** Stream channel response fields used for this UI (names may be omitted from strict CustomChannelData). */
type ChannelDataDisplay = {
  name?: string;
  created_at?: string | Date;
};

/**
 * Map Stream Chat channel to a user-visible channel title.
 */
export function getChannelDisplayName(channel: Channel | null | undefined): string {
  const name = (channel?.data as ChannelDataDisplay | undefined)?.name;
  if (name && String(name).trim().length > 0) {
    return String(name);
  }
  return 'Unnamed channel';
}

/**
 * Parse channel.data.created_at (ISO string or Date) for display. Returns null if missing/invalid.
 */
export function getChannelCreatedAtDate(channel: Channel | null | undefined): Date | null {
  const raw = (channel?.data as ChannelDataDisplay | undefined)?.created_at;
  if (raw == null) {
    return null;
  }
  const d = typeof raw === 'string' ? new Date(raw) : raw;
  if (!(d instanceof Date) || isNaN(d.getTime())) {
    return null;
  }
  return d;
}

/**
 * Build member rows from channel.state.members, online first then by name.
 */
export function getSortedMemberRows(
  members: Record<string, { user_id?: string; user?: { id?: string; name?: string; online?: boolean } }> | undefined
): ChannelMemberRow[] {
  if (!members) {
    return [];
  }
  const rows: ChannelMemberRow[] = Object.values(members).map((m: any) => {
    const id = (m.user_id ?? m.user?.id ?? '') as string;
    const name = (m.user?.name as string | undefined) || id || 'Unknown';
    const online = m.user?.online === true;
    return { id, name, online };
  });
  return rows
    .filter((r) => r.id)
    .sort((a, b) => {
      if (a.online !== b.online) {
        return a.online ? -1 : 1;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
}
